import { Trans } from '@lingui/macro';
import { CircularProgress, LinearProgress, Typography } from '@material-ui/core';
import HighchartsReact from 'highcharts-react-official';
import Highcharts from 'highcharts';
import highchartsMore from 'highcharts/highcharts-more';
import solidGauge from 'highcharts/modules/solid-gauge';
import noDataToDisplay from 'highcharts/modules/no-data-to-display';
import { Phone } from '@material-ui/icons';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import { useState } from 'react';
import _ from 'lodash';
import clsx from 'clsx';
import { useResultStyles } from './VoipTestNowStyles';
import VoipTestNowResultsCallMetricsTable from './VoipTestNowResultsCallMetricsTable';
import VoipTestNowResultsCallFlow from './VoipTestNowResultsCallFlow';
import { jitterSquareColor, latencySquareColor, lossSquareColor } from './VoipPulseTestHelpers';
import useIsPrinting from '../../../utilities/PrintUtilities';

highchartsMore(Highcharts);
solidGauge(Highcharts);
noDataToDisplay(Highcharts);

const TAB_CALL_QUALITY = 0;
const TAB_METRICS = 1;
const TAB_CALL_FLOW = 2;

function CallQualityTab({ results, classes }) {
    return (
        results.config.chart && (
            <div className={classes.tabContentQuality}>
                <table className={classes.qualityChartsTable}>
                    <tbody>
                        <tr>
                            {_.map(results.config.chart, (type, typeKey) => (
                                <td key={typeKey} className={classes.chartTd}>
                                    {_.map(type, (metric, metricKey) => (
                                        <HighchartsReact key={metricKey} highcharts={Highcharts} options={metric} />
                                    ))}
                                </td>
                            ))}
                            {!results.isVoipPulse && <td />}
                        </tr>
                    </tbody>
                </table>
            </div>
        )
    );
}

function MetricsTab({ results, mosColor, classes }) {
    return (
        <div className={classes.metricsContainer} style={{ paddingLeft: 40 }}>
            <VoipTestNowResultsCallMetricsTable results={results} mosColor={mosColor} classes={classes} />
        </div>
    );
}

function CallFlowTab({ results, callFlows, classes }) {
    return !results.flowGathered ? (
        <>
            <CircularProgress color="inherit" />
            <Typography>Gathering call flow information...</Typography>
        </>
    ) : (
        <VoipTestNowResultsCallFlow results={results} callFlows={callFlows} classes={classes} />
    );
}

export default function VoipTestNowResultsChartsAndMetrics({ results, mosColor, callFlows }) {
    const classes = useResultStyles();
    const [tab, setTab] = useState(0);
    const isPrinting = useIsPrinting();

    if (!results?.test?.name) return null;

    const handleTabChange = (newTab) => {
        setTab(newTab);
    };

    // When generating a PDF, render every tab section stacked, each preceded
    // by a print-only header. This matches the legacy portal/ui behavior where
    // `@media print` revealed all `md-tab-content` plus `.print-tab-header`.
    const renderTabContent = () => {
        if (isPrinting) {
            return (
                <>
                    <section className={classes.printTabSection}>
                        <div className={classes.printTabHeader}>
                            <Trans>Call Quality</Trans>
                        </div>
                        <CallQualityTab results={results} classes={classes} />
                    </section>
                    <section className={classes.printTabSection}>
                        <div className={classes.printTabHeader}>
                            <Trans>Metrics</Trans>
                        </div>
                        <MetricsTab results={results} mosColor={mosColor} classes={classes} />
                    </section>
                    <section className={classes.printTabSection}>
                        <div className={classes.printTabHeader}>
                            <Trans>Call Flow</Trans>
                        </div>
                        <CallFlowTab results={results} callFlows={callFlows} classes={classes} />
                    </section>
                </>
            );
        }

        return (
            <>
                {tab === TAB_CALL_QUALITY && <CallQualityTab results={results} classes={classes} />}
                {tab === TAB_METRICS && <MetricsTab results={results} mosColor={mosColor} classes={classes} />}
                {tab === TAB_CALL_FLOW && <CallFlowTab results={results} callFlows={callFlows} classes={classes} />}
            </>
        );
    };

    return (
        <>
            <table className={classes.partyTable}>
                <tbody>
                    <tr>
                        <td className={classes.partyTableColumn} valign="top" align="center">
                            <div className={classes.partySummaryBlock}>
                                {results.isError && (
                                    <div className={clsx(classes.text, classes.error)}>
                                        <Trans>{results.bar.caller.status}</Trans>
                                    </div>
                                )}
                                {results.testRunning && (
                                    <>
                                        <div className={classes.text}>
                                            <Trans>{results.bar.caller.status}</Trans>
                                        </div>
                                        <div className={classes.callProgressWrapper}>
                                            <LinearProgress
                                                className={classes.callProgress}
                                                style={{ height: 6, width: 460, borderRadius: 3 }}
                                                variant="determinate"
                                                value={results.bar.caller.val}
                                            />
                                        </div>
                                    </>
                                )}
                                {results.callerGauge && (
                                    <div className={classes.gaugeWrapper}>
                                        <HighchartsReact highcharts={Highcharts} options={results.callerGauge} />
                                    </div>
                                )}
                                <table className={classes.partyMetricsTable}>
                                    <tbody>
                                        <tr>
                                            <td>
                                                <Trans>Loss</Trans>
                                            </td>
                                            <td>
                                                <Trans>Latency</Trans>
                                            </td>
                                            <td>
                                                <Trans>Jitter</Trans>
                                            </td>
                                        </tr>
                                        <tr>
                                            <td>
                                                <i
                                                    className={lossSquareColor(results.config.results.caller.avgs.loss)}
                                                    aria-hidden="true"
                                                />
                                                {results.config.results.caller.avgsFmt.loss}
                                            </td>
                                            <td>
                                                <i
                                                    className={latencySquareColor(
                                                        results.config.results.caller.avgs.latency
                                                    )}
                                                    aria-hidden="true"
                                                />
                                                {results.config.results.caller.avgsFmt.latency}
                                            </td>
                                            <td>
                                                <i
                                                    className={jitterSquareColor(
                                                        results.config.results.caller.avgs.jitter
                                                    )}
                                                    aria-hidden="true"
                                                />
                                                {results.config.results.caller.avgsFmt.jitter}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </td>
                        <td className={classes.partyTableColumn} valign="top" align="center">
                            {results.isVoipPulse && (
                                <div className={classes.partySummaryBlock}>
                                    {results.isError && (
                                        <div className={clsx(classes.text, classes.error)}>
                                            <Trans>{results.bar.callee.status}</Trans>
                                        </div>
                                    )}
                                    {results.testRunning && (
                                        <>
                                            <div className={classes.text}>
                                                <Trans>{results.bar.callee.status}</Trans>
                                            </div>
                                            <div className={classes.callProgressWrapper}>
                                                <LinearProgress
                                                    className={classes.callProgress}
                                                    style={{ height: 6, width: 460, borderRadius: 3 }}
                                                    variant="determinate"
                                                    value={results.bar.callee.val}
                                                />
                                            </div>
                                        </>
                                    )}
                                    {results.calleeGauge && (
                                        <div className={classes.gaugeWrapper}>
                                            <HighchartsReact highcharts={Highcharts} options={results.calleeGauge} />
                                        </div>
                                    )}
                                    <table className={classes.partyMetricsTable}>
                                        <tbody>
                                            <tr>
                                                <td>
                                                    <Trans>Loss</Trans>
                                                </td>
                                                <td>
                                                    <Trans>Latency</Trans>
                                                </td>
                                                <td>
                                                    <Trans>Jitter</Trans>
                                                </td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    {/* <i ng-class="results.lossSquareColor(results.config.results.callee.avgs.loss)" */}
                                                    <i aria-hidden="true" />{' '}
                                                    {results.config.results.callee.avgsFmt.loss}
                                                </td>
                                                <td>
                                                    {/* <i ng-class="results.latencySquareColor(results.config.results.callee.avgs.latency)" */}
                                                    <i aria-hidden="true" />{' '}
                                                    {results.config.results.callee.avgsFmt.latency}
                                                </td>
                                                <td>
                                                    {/* <i ng-class="results.jitterSquareColor(results.config.results.callee.avgs.jitter)" */}
                                                    <i aria-hidden="true" />{' '}
                                                    {results.config.results.callee.avgsFmt.jitter}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            {!results.isVoipPulse && (
                                <div className={classes.phoneContainer}>
                                    <div className={classes.phoneNumberText}>{results.test.testConfig.phone}</div>
                                    <Phone className={classes.mdIcon} />
                                </div>
                            )}
                        </td>
                    </tr>
                </tbody>
            </table>
            <Tabs
                className={clsx(classes.tabs, isPrinting && classes.printHiddenTabs)}
                value={tab}
                onChange={(e, index) => handleTabChange(index)}
            >
                <Tab className={classes.tab} id="callQuality" label={<Trans>Call Quality</Trans>} />
                <Tab className={classes.tab} id="metrics" label={<Trans>Metrics</Trans>} />
                <Tab className={classes.tab} id="callFlow" label={<Trans>Call Flow</Trans>} />
            </Tabs>
            {renderTabContent()}
        </>
    );
}

VoipTestNowResultsChartsAndMetrics.defaultProps = {
    chartHeight: 150,
};
